/* istanbul ignore file */

import type { Client } from 'urql';
import {
  fetchGooglePlacesAddress,
  formatGoogleAddress as formatDeliveryAddress,
  type GooglePlacesAddress,
  type RadarAddress,
  slugify,
} from '@sg/garnish';
import { type Address, type LocationStatus } from '@sg/graphql-schema';

import {
  findExistingDeliveryAddress,
  withoutCountryTerm as withoutRadarCountryTerm,
} from '@order/features/locations';

import { type OrdersV2ForLocationQuery } from '../../../../../graphql/operations.generated';
import type { CustomerAddressesQuery } from '../../graphql/CustomerDeliveryDetails.generated';
import type {
  DeliveryLocationByAddressIdQueryVariables,
  DeliveryLocationByAddressQueryVariables,
  DeliveryLocationByAddressV2QueryVariables,
} from '../../graphql/DeliveryLocationByAddress.generated';
import type {
  AddressDetails,
  AddressWithoutIds,
  DeliveryLocationWithoutNearbyStore,
  Location,
} from '../../LocationSearch.types';
import {
  activeCartDeliveryDetailsQuery,
  customerAddressesQuery,
  deliveryLocationByAddressIdQuery,
  deliveryLocationByAddressQuery,
  deliveryLocationByAddressV2Query,
  deliveryLocationByAddressWithDisclosureFieldsQuery,
  recentLocationsQuery,
  recentLocationsWithDisclosureQuery,
} from '../../queries';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetches the delivery location using the provided Google place ID.
 */
export async function getDeliveryLocationByPlaceId(
  props: GetDeliveryLocationByPlaceIdProps,
): Promise<Location | DeliveryLocationWithoutNearbyStore | undefined> {
  const {
    client,
    addressDetails,
    googlePlacesApiKey,
    googlePlaceId,
    googlePlaceAddress,
    shouldUseDisclosureFields,
  } = props;

  // ─── By Address ID ───────────────────────────────────────────────────

  if (addressDetails) {
    return getDeliveryLocationDetailsByAddressId({
      client,
      addressDetails,
      shouldUseDisclosureFields,
    });
  }

  // ─── By Google Place ID ──────────────────────────────────────────────

  if (!googlePlaceId) return;

  const formattedGooglePlaceAddress = await fetchGooglePlacesAddress({
    placeId: googlePlaceId,
    placeAddress: googlePlaceAddress,
    apiKey: googlePlacesApiKey,
  });

  return getDeliveryLocationDetails({
    client,
    googlePlaceAddress: formattedGooglePlaceAddress,
    shouldUseDisclosureFields,
  });
}

/**
 * Fetches delivery location details using coordinates + address.
 */
export async function getDeliveryLocationDetailsByAddress(
  params: GetDeliveryLocationDetailsByAddressParams,
): Promise<Location | DeliveryLocationWithoutNearbyStore> {
  const { client, address } = params;

  if (!address) {
    throw new Error(
      'Failed to get delivery location details. No address passed in.',
    );
  }

  const deliveryLocation =
    'addressId' in address
      ? await getDeliveryLocationByAddressId({
          client,
          input: { addressId: address.addressId },
        })
      : await getDeliveryLocationV2({
          client,
          input: {
            lat: address.latitude,
            lon: address.longitude,
            address1: address.street,
            city: address.city,
            state: address.state,
            zipcode: Number(address.zipCode),
          },
        });

  if (!deliveryLocation) {
    throw new Error('Failed to get delivery location details');
  }

  // ─── Delivery Location Without Nearby Store ──────────────────────────

  if (deliveryLocation.__typename === 'NoValidRestaurants') {
    return getWithoutNearbyStoreDeliveryLocationDetails(address);
  }

  // ─── Delivery Location With Nearby Store ─────────────────────────────

  return getWithNearbyStoreDeliveryLocationDetails({
    client,
    addressDetails: address,
    deliveryLocation,
  });
}

/**
 * Fetches delivery location details based on the provided Google place.
 */
export async function getDeliveryLocationDetails(
  params: GetDeliveryLocationDetailsParams,
): Promise<Location | DeliveryLocationWithoutNearbyStore> {
  const { client, googlePlaceAddress, shouldUseDisclosureFields } = params;

  const deliveryLocation = await getDeliveryLocation({
    client,
    input: convertAddressToDeliveryLocationInput(googlePlaceAddress),
    shouldUseDisclosureFields,
  });

  if (!deliveryLocation) {
    throw new Error('Failed to get delivery location details');
  }

  // ─── Delivery Location Without Nearby Store ──────────────────────────

  if (deliveryLocation.__typename === 'NoValidRestaurants') {
    return getWithoutNearbyStoreDeliveryLocationDetails(googlePlaceAddress);
  }

  // ─── Delivery Location With Nearby Store ──────────────────────────

  return getWithNearbyStoreDeliveryLocationDetails({
    client,
    addressDetails: googlePlaceAddress,
    deliveryLocation,
  });
}

/**
 * Fetches delivery location details based on the provided address.
 */
export async function getDeliveryLocationDetailsByAddressId(
  params: GetDeliveryLocationDetailsByAddressIdParams,
): Promise<Location | DeliveryLocationWithoutNearbyStore> {
  const { client, addressDetails, shouldUseDisclosureFields } = params;

  const deliveryLocation = await getDeliveryLocation({
    client,
    input: { addressId: addressDetails.addressId },
    shouldUseDisclosureFields,
  });

  if (!deliveryLocation) {
    throw new Error('Failed to get delivery location details');
  }

  // ─── Delivery Location Without Nearby Store ──────────────────────────

  if (deliveryLocation.__typename === 'NoValidRestaurants') {
    return getWithoutNearbyStoreDeliveryLocationDetails(addressDetails);
  }

  // ─── Delivery Location With Nearby Store ──────────────────────────

  return getWithNearbyStoreDeliveryLocationDetails({
    client,
    addressDetails,
    deliveryLocation,
  });
}

/**
 * Fetches a nearby location for the specified delivery address.
 */
async function getDeliveryLocation(params: GetDeliveryLocationParams) {
  const { client, input, shouldUseDisclosureFields } = params;

  // NOTE: Conditionally fetch extra disclosure fields.
  const query = shouldUseDisclosureFields
    ? deliveryLocationByAddressWithDisclosureFieldsQuery
    : deliveryLocationByAddressQuery;

  const { data } = await query(client, { input });
  const { locationByAddress } = data ?? {};

  if (
    locationByAddress?.__typename !== 'Location' &&
    locationByAddress?.__typename !== 'NoValidRestaurants'
  ) {
    throw new Error('Failed to get delivery location');
  }

  return locationByAddress;
}

async function getDeliveryLocationV2(params: GetDeliveryLocationV2Params) {
  const { client, input } = params;

  const { data } = await deliveryLocationByAddressV2Query(client, { input });
  const { locationByAddressV2 } = data ?? {};

  if (
    locationByAddressV2?.__typename !== 'Location' &&
    locationByAddressV2?.__typename !== 'NoValidRestaurants'
  ) {
    throw new Error('Failed to get delivery location');
  }

  return locationByAddressV2;
}

async function getDeliveryLocationByAddressId(
  params: GetDeliveryLocationByAddressIdParams,
) {
  const { client, input } = params;

  const { data } = await deliveryLocationByAddressIdQuery(client, { input });
  const { locationByAddressId } = data ?? {};

  if (
    locationByAddressId?.__typename !== 'Location' &&
    locationByAddressId?.__typename !== 'NoValidRestaurants'
  ) {
    throw new Error('Failed to get delivery location');
  }

  return locationByAddressId;
}

/**
 * Fetches the active cart delivery location (if available).
 */
export async function getActiveCartDeliveryLocation(
  props: GetActiveCartDeliveryLocationProps,
) {
  const {
    client,
    googlePlacesApiKey,
    shouldUseDisclosureFields,
    shouldUseImprovedDeliverySearch,
  } = props;

  const { data } = await activeCartDeliveryDetailsQuery(client);
  const { cart } = data ?? {};

  // ─────────────────────────────────────────────────────────────────────

  const addressDetails = cart?.deliveryOrderDetail?.address;
  const activeCartDeliveryLocationGooglePlaceId = addressDetails?.googlePlaceId;
  const activeCartDeliveryLocationAddressId = addressDetails?.id;

  // ─── By Address ID ───────────────────────────────────────────────────

  if (shouldUseImprovedDeliverySearch && activeCartDeliveryLocationAddressId) {
    return getDeliveryLocationByPlaceId({
      client,
      addressDetails: convertToAddressDetails(addressDetails),
      googlePlacesApiKey,
      googlePlaceId: activeCartDeliveryLocationGooglePlaceId,
      shouldUseDisclosureFields,
    });
  }

  // ─── By Google Place ID ──────────────────────────────────────────────

  if (!activeCartDeliveryLocationGooglePlaceId) return;

  return getDeliveryLocationByPlaceId({
    client,
    googlePlaceId: activeCartDeliveryLocationGooglePlaceId,
    googlePlacesApiKey,
    shouldUseDisclosureFields,
  });
}

/**
 * Fetches the last interacted store delivery location (if available).
 */
export async function getLastInteractedStoreDeliveryLocation(
  props: GetLastInteractedStoreDeliveryLocation,
) {
  const {
    client,
    googlePlacesApiKey,
    lastInteractedStoreAddressId,
    shouldUseDisclosureFields,
    shouldUseImprovedDeliverySearch,
  } = props;

  const lastInteractedStore = await getCustomerDeliveryAddressByAddressId(
    client,
    lastInteractedStoreAddressId,
  );
  const lastInteractedStoreGooglePlaceId = lastInteractedStore?.googlePlaceId;

  // ─── By Address ID ───────────────────────────────────────────────────

  if (shouldUseImprovedDeliverySearch && lastInteractedStore) {
    return getDeliveryLocationByPlaceId({
      client,
      addressDetails: convertToAddressDetails(lastInteractedStore),
      googlePlaceId: lastInteractedStoreGooglePlaceId,
      googlePlacesApiKey,
      shouldUseDisclosureFields,
    });
  }

  // ─── By Google Place ID ──────────────────────────────────────────────

  if (!lastInteractedStoreGooglePlaceId) return;

  return getDeliveryLocationByPlaceId({
    client,
    googlePlaceId: lastInteractedStoreGooglePlaceId,
    googlePlacesApiKey,
    shouldUseDisclosureFields,
  });
}

/**
 * Attempts to get the last placed order delivery address ID.
 */
export async function getLastPlacedOrderDeliveryLocation(
  props: GetLastPlacedOrderDeliveryLocation,
) {
  const {
    client,
    googlePlacesApiKey,
    shouldUseDisclosureFields,
    shouldUseImprovedDeliverySearch,
  } = props;

  const { data: customerData } = await customerAddressesQuery(client);
  const isSignedInCustomer = customerData?.currentCustomer?.id !== undefined;

  // NOTE: We only query the last placed order only for signed-in customers.
  if (!isSignedInCustomer) return;

  const customerDeliveryAddress = await getCustomerDeliveryAddresses(client);
  const query = shouldUseDisclosureFields
    ? recentLocationsWithDisclosureQuery
    : recentLocationsQuery;

  const { data } = await query(client, 'cache-first');
  const { ordersV2: ordersData } = data ?? {};

  const hasLastPlacedOrder = ordersData?.__typename === 'OrdersResponseSuccess';
  const lastPlacedOrderDetails = hasLastPlacedOrder
    ? findLastPlacedValidDeliveryOrder(ordersData.orders, customerDeliveryAddress) // prettier-ignore
    : undefined;

  const lastPlacedOrder = lastPlacedOrderDetails?.lastPlacedOrder;
  const lastPlacedOrderGooglePlaceId = (
    lastPlacedOrder?.deliveryOrderDetail as any
  )?.address?.googlePlaceId;

  // ─── By Address ID ───────────────────────────────────────────────────

  if (
    shouldUseImprovedDeliverySearch &&
    lastPlacedOrderDetails?.customerDeliveryAddress
  ) {
    return getDeliveryLocationByPlaceId({
      client,
      addressDetails: convertToAddressDetails(lastPlacedOrderDetails.customerDeliveryAddress), // prettier-ignore
      googlePlaceId: lastPlacedOrderGooglePlaceId,
      googlePlacesApiKey,
      shouldUseDisclosureFields,
    });
  }

  // ─── By Google Place ID ──────────────────────────────────────────────

  if (!lastPlacedOrderGooglePlaceId) return;

  return getDeliveryLocationByPlaceId({
    client,
    googlePlaceId: lastPlacedOrderGooglePlaceId,
    googlePlacesApiKey,
    shouldUseDisclosureFields,
  });
}

/**
 * Attempts to get the customer first previously saved delivery location.
 */
export async function getCustomerFirstDeliveryLocation(
  props: GetCustomerFirstDeliveryLocation,
) {
  const {
    client,
    googlePlacesApiKey,
    shouldUseDisclosureFields,
    shouldUseImprovedDeliverySearch,
  } = props;

  const customerDeliveryAddresses = await getCustomerDeliveryAddresses(client);

  const customerFirstDeliveryAddressGooglePlaceId =
    customerDeliveryAddresses?.[0]?.googlePlaceId;

  // ─── By Address ID ───────────────────────────────────────────────────

  if (shouldUseImprovedDeliverySearch && customerDeliveryAddresses?.[0]) {
    return getDeliveryLocationByPlaceId({
      client,
      addressDetails: convertToAddressDetails(customerDeliveryAddresses[0]),
      googlePlacesApiKey,
      googlePlaceId: customerFirstDeliveryAddressGooglePlaceId,
      shouldUseDisclosureFields,
    });
  }

  // ─── By Google Place ID ──────────────────────────────────────────────

  if (!customerFirstDeliveryAddressGooglePlaceId) return;

  return getDeliveryLocationByPlaceId({
    client,
    googlePlaceId: customerFirstDeliveryAddressGooglePlaceId,
    googlePlacesApiKey,
    shouldUseDisclosureFields,
  });
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Attempts to get customer's delivery addresses.
 */
export async function getCustomerDeliveryAddresses(client: Client) {
  const { data } = await customerAddressesQuery(client);

  return data?.currentCustomer?.addresses ?? [];
}

/**
 * Attempts to get the customer's delivery address based on the provided address ID.
 */
export async function getCustomerDeliveryAddressByAddressId(
  client: Client,
  addressId: string,
) {
  const customerAddresses = await getCustomerDeliveryAddresses(client);

  return customerAddresses.find(
    (customerAddress) => customerAddress.id === addressId,
  );
}

/**
 * Attempts to get the customer's delivery address based on the provided Google place ID.
 */
export async function getCustomerDeliveryAddressByGooglePlaceId(
  client: Client,
  googlePlaceId: string,
) {
  const customerAddresses = await getCustomerDeliveryAddresses(client);

  return customerAddresses.find(
    (customerAddress) => customerAddress.googlePlaceId === googlePlaceId,
  );
}

/**
 * Attempts to get an existing customer delivery address name, based on the provided Google place ID.
 */
export async function getDeliveryAddressName(
  client: Client,
  googlePlaceId: string,
) {
  const existingAddress = await getCustomerDeliveryAddressByGooglePlaceId(
    client,
    googlePlaceId,
  );

  return existingAddress?.name;
}

/**
 * Attempts to get an existing customer delivery address ID, based on the provided Google place ID.
 */
export async function getExistingCustomerDeliveryAddressId(
  client: Client,
  googlePlaceId: string,
) {
  const existingAddress = await getCustomerDeliveryAddressByGooglePlaceId(
    client,
    googlePlaceId,
  );

  return existingAddress?.id;
}

/**
 * Attempts to find a previously placed delivery order.
 */
function findLastPlacedValidDeliveryOrder(
  lastPlacedOrders: LastPlacedOrders,
  customDeliveryAddresses: CustomerDeliveryAddresses,
) {
  const customDeliveryAddressesSet = new Set(
    customDeliveryAddresses.map(({ id }) => id),
  );

  const lastPlacedOrder = lastPlacedOrders.find(({ deliveryOrderDetail }) => {
    const deliveryAddressId = deliveryOrderDetail?.address?.id;

    return (
      deliveryAddressId !== undefined &&
      typeof deliveryAddressId === 'string' &&
      customDeliveryAddressesSet.has(deliveryAddressId)
    );
  });

  return {
    lastPlacedOrder: lastPlacedOrder,
    customerDeliveryAddress: customDeliveryAddresses.find(
      ({ id }) => id === lastPlacedOrder?.deliveryOrderDetail?.address?.id,
    ),
  };
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns delivery location (WITH nearby store) details based on the provided
 * Google Place address.
 */
async function getWithNearbyStoreDeliveryLocationDetails(
  props: GetWithNearbyStoreDeliveryLocationDetails,
): Promise<Location> {
  const { client, addressDetails, deliveryLocation } = props;

  if ('googlePlaceId' in addressDetails && addressDetails.googlePlaceId) {
    const { googlePlaceId } = addressDetails;

    const deliveryAddressId = await getExistingCustomerDeliveryAddressId(
      client,
      googlePlaceId,
    );
    const deliveryAddressName = await getDeliveryAddressName(
      client,
      googlePlaceId,
    );

    return extractDeliveryLocationDetails({
      deliveryLocation,
      deliveryAddressId,
      deliveryAddressName,
      address: addressDetails,
    });
  }

  const customerAddresses = await getCustomerDeliveryAddresses(client);
  const existingDeliveryAddress = findExistingDeliveryAddress(
    customerAddresses,
    addressDetails,
  );

  const deliveryAddressName = existingDeliveryAddress?.name;
  const deliveryAddressId = existingDeliveryAddress?.id;

  // ─────────────────────────────────────────────────────────────────────

  return extractDeliveryLocationDetails({
    deliveryLocation,
    deliveryAddressId,
    deliveryAddressName,
    address: addressDetails,
  });
}

/**
 * Returns delivery location (WITH nearby store) details based on the provided
 * Google Place address.
 */
export function extractDeliveryLocationDetails(
  params: ExtractDeliveryLocationDetailsParams,
): Location {
  const { address, deliveryAddressId, deliveryAddressName, deliveryLocation } =
    params;

  const { longitude, latitude, city, state, country, zipCode, street } =
    address;

  const googlePlaceId =
    'googlePlaceId' in address ? address.googlePlaceId : undefined;

  const estimatedDeliveryTime = getDeliveryLocationETA(deliveryLocation);
  const deliveryFormattedAddress = formatDeliveryAddress(address);
  const isDeliveryLocationClosed = deliveryLocation?.status === 'CLOSED';

  const deliveryLocationRestaurant = deliveryLocation?.restaurant ?? {};
  const deliveryLocationRestaurantWithDisclosure =
    deliveryLocationRestaurant as unknown as ValidDeliveryLocation & {
      showDeliveryPriceDifferenciationDisclosure: boolean;
    };

  const {
    id: restaurantId,
    name: restaurantName,
    slug: restaurantSlug,
    flexMessage,
    phone,
    deliveryFee,
  } = deliveryLocationRestaurant;
  const { name: deliveryVendorName, restaurantId: deliveryVendorRestaurantId } =
    deliveryLocation.vendor;

  // ─────────────────────────────────────────────────────────────────────

  return {
    isDelivery: true,
    id: googlePlaceId ?? slugify(deliveryFormattedAddress),
    addressId: deliveryAddressId ?? '',
    name: deliveryAddressName ?? '',
    address: deliveryFormattedAddress ?? '',
    estimatedDeliveryTime,
    flexMessage,
    phone,
    isClosed: isDeliveryLocationClosed,
    lat: latitude,
    lng: longitude,
    street,
    zipCode,
    city,
    state,
    country,
    restaurantSlug,
    restaurantName,
    restaurantId,
    restaurantDeliveryFee: deliveryFee,
    deliveryVendorName,
    deliveryVendorRestaurantId,
    showDeliveryPriceDifferenciationDisclosure:
      deliveryLocationRestaurantWithDisclosure.showDeliveryPriceDifferenciationDisclosure ??
      false,
  };
}

/**
 * Returns delivery location (WITHOUT nearby store) details based on the provided
 * Google Place address.
 */
function getWithoutNearbyStoreDeliveryLocationDetails(
  address: AddressDetails | AddressWithoutIds | GooglePlacesAddress,
): DeliveryLocationWithoutNearbyStore {
  const deliveryFormattedAddress = formatDeliveryAddress(address);

  const maybePlaceId = 'googlePlaceId' in address ? address.googlePlaceId : undefined; // prettier-ignore
  const sluggedFormattedAddress = slugify(deliveryFormattedAddress);

  return {
    id: maybePlaceId ?? sluggedFormattedAddress,
    lat: address.latitude,
    lng: address.longitude,
    street: address.street,
    zipCode: address.zipCode,
    city: address.city,
    state: address.state,
    country: address.country,
    address: deliveryFormattedAddress,
    name: '',
    restaurantSlug: undefined,
    restaurantId: '',
    restaurantName: '',
    addressId: undefined,
    deliveryVendorName: undefined,
    deliveryVendorRestaurantId: undefined,
    restaurantDeliveryFee: undefined,
  };
}

/**
 * Returns a formatted location address that does not include the country term.
 */
function withoutCountryTerm(
  address: string,
  terms: ReadonlyArray<Readonly<{ value: string }>>,
) {
  const countryTerm = terms.at(-1)?.value;
  const countrySubstring = `, ${countryTerm}`;

  if (address.endsWith(countrySubstring)) {
    return address.replace(countrySubstring, '');
  }

  return address;
}

/**
 * Returns a formatter function for Google Autocomplete Predictions
 */
export function formatDeliveryAddressPrediction(
  customerDeliveryAddresses: CustomerDeliveryAddresses,
) {
  return function (predictionAddress: AutocompletePrediction) {
    const {
      place_id: id,
      description,
      terms,
      matched_substrings: matchedSubstrings,
    } = predictionAddress;

    const customerExistingDeliveryAddressName =
      getCustomerExistingDeliveryAddressName(customerDeliveryAddresses, id);
    const name = customerExistingDeliveryAddressName ?? '';

    const address = withoutCountryTerm(description, terms);

    return { id, name, address, matchedSubstrings, description };
  };
}

/**
 * Returns a formatter function for Radar Autocomplete Predictions
 */
export function formatRadarDeliveryAddressPrediction(
  customerDeliveryAddresses: CustomerDeliveryAddresses,
) {
  return function (predictionAddress: RadarAddress) {
    const {
      latitude,
      longitude,
      addressLabel: street,
      city,
      stateCode: state,
      postalCode: zipCode,
      countryCode: country,
      formattedAddress,
    } = predictionAddress;

    const existingDeliveryAddress = findExistingDeliveryAddress(
      customerDeliveryAddresses,
      {
        street,
        city,
        state,
        zipCode,
        latitude,
        longitude,
        country,
        formattedAddress,
      },
    );

    const id = existingDeliveryAddress?.id ?? slugify(formattedAddress);
    const name = existingDeliveryAddress?.name ?? '';

    const address = withoutRadarCountryTerm(predictionAddress);

    return {
      id,
      name,
      address,
      latitude,
      longitude,
      street,
      city,
      state,
      zipCode,
      country,
    };
  };
}

/**
 * Returns estimated delivery time for the provided delivery location.
 */
function getDeliveryLocationETA(
  deliveryLocation: MaybeDeliveryLocationWithETA,
) {
  if (!deliveryLocation?.estimateRange) return;

  const { start = 0, end = 0 } = deliveryLocation.estimateRange;

  return start === end ? `${start}` : `${start}-${end}`;
}

/**
 * Attempts to get the name of customer's matching delivery address based on the provided Google Place ID.
 */
function getCustomerExistingDeliveryAddressName(
  customerDeliveryAddresses: CustomerDeliveryAddresses,
  googlePlaceId: string,
) {
  return customerDeliveryAddresses?.find(
    (customerDeliveryAddress) =>
      customerDeliveryAddress.googlePlaceId === googlePlaceId,
  )?.name;
}

/**
 * Convert the specified delivery address into an input
 * object needed to retrieve the delivery location.
 */
function convertAddressToDeliveryLocationInput(
  deliveryAddress: AddressDetails | GooglePlacesAddress,
): DeliveryLocationByAddressQueryVariables['input'] {
  const {
    googlePlaceId,
    longitude: lon,
    latitude: lat,
    street: address1,
    city,
    state,
    zipCode,
  } = deliveryAddress;
  const maybeZipCode = Number.isNaN(zipCode)
    ? {}
    : { zipcode: Number(zipCode) };

  return {
    address1,
    city,
    lat,
    lon,
    state,
    googlePlaceId,
    ...maybeZipCode,
  };
}

function convertToAddressDetails(
  customerDeliveryAddress: Address,
): AddressDetails {
  return {
    ...customerDeliveryAddress,
    formattedAddress: formatDeliveryAddress(customerDeliveryAddress),
    addressId: customerDeliveryAddress.id,
    name: customerDeliveryAddress.name ?? '',
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type GetDeliveryLocationByPlaceIdProps = Readonly<{
  client: Client;
  googlePlacesApiKey: string;
  googlePlaceId: string | undefined;
  googlePlaceAddress?: string;
  addressDetails?: AddressDetails;
}> &
  DisclosureFieldsParams;

type GetDeliveryLocationDetailsParams = {
  client: Client;
  googlePlaceAddress: GooglePlacesAddress;
} & DisclosureFieldsParams;

type GetDeliveryLocationDetailsByAddressIdParams = {
  client: Client;
  addressDetails: AddressDetails;
} & DisclosureFieldsParams;

type GetDeliveryLocationDetailsByAddressParams = {
  client: Client;
  address: AddressDetails | AddressWithoutIds | undefined;
} & DisclosureFieldsParams;

type GetDeliveryLocationParams = {
  client: Client;
  input: DeliveryLocationByAddressQueryVariables['input'];
} & DisclosureFieldsParams;

type GetDeliveryLocationV2Params = {
  client: Client;
  input: DeliveryLocationByAddressV2QueryVariables['input'];
};

type GetDeliveryLocationByAddressIdParams = {
  client: Client;
  input: DeliveryLocationByAddressIdQueryVariables['input'];
};

type GetActiveCartDeliveryLocationProps = Readonly<{
  client: Client;
  googlePlacesApiKey: string;
  shouldUseImprovedDeliverySearch: boolean;
}> &
  DisclosureFieldsParams;

type GetLastInteractedStoreDeliveryLocation = Readonly<{
  client: Client;
  googlePlacesApiKey: string;
  lastInteractedStoreAddressId: string;
  shouldUseImprovedDeliverySearch: boolean;
}> &
  DisclosureFieldsParams;

type GetLastPlacedOrderDeliveryLocation = Readonly<{
  client: Client;
  googlePlacesApiKey: string;
  shouldUseImprovedDeliverySearch: boolean;
}> &
  DisclosureFieldsParams;

type GetCustomerFirstDeliveryLocation = Readonly<{
  client: Client;
  googlePlacesApiKey: string;
  shouldUseImprovedDeliverySearch: boolean;
}> &
  DisclosureFieldsParams;

type GetWithNearbyStoreDeliveryLocationDetails = Readonly<{
  client: Client;
  addressDetails: AddressDetails | AddressWithoutIds | GooglePlacesAddress;
  deliveryLocation: ValidDeliveryLocation;
}>;

type ExtractDeliveryLocationDetailsParams = {
  deliveryAddressId: string | undefined;
  deliveryAddressName: string | undefined | null;
  deliveryLocation: Omit<
    ValidDeliveryLocation,
    '__typename' | 'status' | 'estimateRange'
  > & {
    status?: LocationStatus;
    estimateRange?: { start: number; end: number };
  };
  address: Omit<GooglePlaceAddress, 'formattedAddress'> | AddressWithoutIds;
};

type ValidDeliveryLocation = Extract<
  Awaited<ReturnType<typeof getDeliveryLocation>>,
  Readonly<{ __typename: 'Location' }>
>;

// @ts-expect-error TS(2503): Cannot find namespace 'google'.
type AutocompletePrediction = google.maps.places.AutocompletePrediction;

type GooglePlaceAddress = Awaited<ReturnType<typeof fetchGooglePlacesAddress>>;

type CustomerDeliveryAddresses = NonNullable<
  CustomerAddressesQuery['currentCustomer']
>['addresses'];

type MaybeDeliveryLocationWithETA = Readonly<{
  estimateRange?: {
    start: number;
    end: number;
  };
}>;

type LastPlacedOrders = Extract<
  OrdersV2ForLocationQuery['ordersV2'],
  Readonly<{ __typename: 'OrdersResponseSuccess' }>
>['orders'];

type DisclosureFieldsParams = {
  /**
   * A flag to fetch extra upcharge disclosure fields from BE.
   */
  shouldUseDisclosureFields: boolean;
};
